import { isString } from 'typeDeclarations/typeGuards';

/* ************ */
/* Environments */
/* ************ */
export type AppEnvironment = 'dev' | 'local' | 'prod' | 'release';

const appEnvironment = (import.meta.env.REACT_APP_ENVIRONMENT ?? 'dev') as AppEnvironment;

type EnvironmentString = 'true' | 'false';

const environmentStringToBooleanMap: Record<EnvironmentString, boolean> = {
  true: true,
  false: false,
};

export function isEnvironmentString(variable: string): variable is EnvironmentString {
  return variable in environmentStringToBooleanMap;
}

export function getEnvironmentBool(environmentVariable: unknown, defaultValue = false): boolean {
  if (!isString(environmentVariable)) {
    return defaultValue;
  }

  const lowercaseEnvironmentVariable = environmentVariable.toLowerCase();
  if (!isEnvironmentString(lowercaseEnvironmentVariable)) {
    return defaultValue;
  }

  return environmentStringToBooleanMap[lowercaseEnvironmentVariable];
}

/* ***************** */
/* Local development */
/* ***************** */

const isLocalhost = getEnvironmentBool(import.meta.env.REACT_APP_IS_LOCALHOST, true);

/* ******** */
/* API Keys */
/* ******** */

// TODO: .env files should be cleaned up, so we can start placing these API keys there,
//  instead of laying them around the code.

const STRIPE_PROD_KEY = 'pk_live_ZLgQkUhdkH2UjqTZ2I0rj1ah';
const STRIPE_DEV_KEY = 'pk_test_eJpsQOLQr3ONNnYJW1C60IA5';
const GOOGLE_MAPS_DEV_KEY = 'AIzaSyA9FTO-R2MHjQsR2KVnq7xO4Ptjh_ClDu8';
const GOOGLE_MAPS_PROD_KEY = 'AIzaSyAp81WhapBNr5Fi150jh7_JD5BJJZDStoA';
const GOOGLE_MAPS_LOCALHOST_KEY = 'AIzaSyBAbT8dl0QmMZSv3uM39_8P6PIv9_VU6Rk';

//Facebook APP ID localhost and dev are the same for now
const FACEBOOK_APP_LOCALHOST_KEY = '184289095711299';
const FACEBOOK_APP_DEV_KEY = '184289095711299';
const FACEBOOK_APP_PROD_KEY = '201636377071028';
const POSTHOG_APP_DEV_KEY = 'phc_22U99Ic5GuFRbVyzBr6z4Yg0C8ZX9xY7kmLoJNo3ahY';
const POSTHOG_APP_PROD_KEY = 'phc_cnt2t8CTQGRkORA7VbTeAdAxf4IXOp5duut5pxKvSlD';
const posthogShouldTrack = appEnvironment === 'prod' || appEnvironment === 'release';

const posthogApiKey =
  appEnvironment === 'prod' ? POSTHOG_APP_PROD_KEY : appEnvironment === 'release' ? POSTHOG_APP_DEV_KEY : '';

// Map IDs configured with Administrative Areas in Google Maps Platform for different environments
const GOOGLE_MAPS_MAP_IDS = {
  localhost: '358fe4d20739acd1a66a4a53',
  dev: 'fbdcc638df0bc7b7ea0dde27',
  prod: 'a76a79eaf072c598cf77313e',
};

const stripeKey = appEnvironment === 'prod' && !isLocalhost ? STRIPE_PROD_KEY : STRIPE_DEV_KEY;

const googleMapsApiKey = isLocalhost
  ? GOOGLE_MAPS_LOCALHOST_KEY
  : appEnvironment === 'prod'
    ? GOOGLE_MAPS_PROD_KEY
    : GOOGLE_MAPS_DEV_KEY;

const facebookAppID = isLocalhost
  ? FACEBOOK_APP_LOCALHOST_KEY
  : appEnvironment === 'prod'
    ? FACEBOOK_APP_PROD_KEY
    : FACEBOOK_APP_DEV_KEY;

// Determine which Map ID to use based on environment
const googleMapsBoundaryMapId = isLocalhost
  ? GOOGLE_MAPS_MAP_IDS.localhost
  : appEnvironment === 'prod'
    ? GOOGLE_MAPS_MAP_IDS.prod
    : GOOGLE_MAPS_MAP_IDS.dev;

/* ********** */
/* App Themes */
/* ********** */

/**
 * Possible themes from the REACT_APP_THEME env var
 */
export type ThemeName =
  | 'default'
  | 'leadzai'
  | 'yp-ca'
  | 'sitee'
  | 'bizay'
  | 'thryv'
  | 'fcr-be'
  | 'youvia'
  | 'directel'
  | 'puxenegc'
  | 'inovcorp'
  | 'mediamag'
  | 'paginas-amarelas'
  | 'pag-amrl'
  | 'pblcrEst'
  | 'fonecta'
  | 'newmed21'
  | 'herold'
  | 'leadgen'
  | 'bee-ddt'
  | 'medialuo';

export const themeNames: Record<string, ThemeName> = {
  default: 'default',
  leadzai: 'leadzai',
  fcr: 'fcr-be',
  bizay: 'bizay',
  sitee: 'sitee',
  thryv: 'thryv',
  youvia: 'youvia',
  'yp-ca': 'yp-ca',
  directel: 'directel',
  puxenegc: 'puxenegc',
  inovcorp: 'inovcorp',
  'paginas-amarelas': 'paginas-amarelas',
  'pag-amrl': 'pag-amrl',
  pblcrEst: 'pblcrEst',
  mediamag: 'mediamag',
  fonecta: 'fonecta',
  newmed21: 'newmed21',
  herold: 'herold',
  leadgen: 'leadgen',
  'bee-ddt': 'bee-ddt',
  medialuo: 'medialuo',
};

interface ThemeConfig {
  title: string;
  altLogo: boolean;
}

export const themeConfig: Record<ThemeName, ThemeConfig> = {
  default: {
    title: 'Leadzai Platform | AI-Driven Digital Advertising Platform',
    altLogo: false,
  },
  leadzai: {
    title: 'Leadzai Platform | AI-Driven Digital Advertising Platform',
    altLogo: false,
  },
  'fcr-be': {
    title: 'Omni by FCR Media',
    altLogo: false,
  },
  bizay: {
    title: '360imprimir',
    altLogo: false,
  },
  sitee: {
    title: 'Sitee',
    altLogo: false,
  },
  thryv: {
    title: 'Thryv',
    altLogo: false,
  },
  youvia: {
    title: 'Youvia',
    altLogo: false,
  },
  'yp-ca': {
    title: 'Omni by Yellow Pages',
    altLogo: false,
  },
  directel: {
    title: 'Directel',
    altLogo: false,
  },
  puxenegc: {
    title: 'Puxe Negócios',
    altLogo: false,
  },
  inovcorp: {
    title: 'Inovcorp',
    altLogo: false,
  },
  'paginas-amarelas': {
    title: 'EasyAds - go-online.pt',
    altLogo: false,
  },
  'pag-amrl': {
    title: 'EasyAds - go-online.pt',
    altLogo: false,
  },
  pblcrEst: {
    title: 'Publicar',
    altLogo: false,
  },
  mediamag: {
    title: 'MediaMagneten',
    altLogo: false,
  },
  fonecta: {
    title: 'Fonecta',
    altLogo: true,
  },
  newmed21: {
    title: 'New Media 21',
    altLogo: false,
  },
  herold: {
    title: 'Herold',
    altLogo: false,
  },
  leadgen: {
    title: 'LeadGen Hub',
    altLogo: false,
  },
  'bee-ddt': {
    title: 'BeeDigital',
    altLogo: false,
  },
  medialuo: {
    title: 'Medialuo',
    altLogo: false,
  },
};

export const THEME_TO_COUNTRY_PRETTY_ID: Record<string, string | null> = {
  [themeNames.default]: 'US',
  [themeNames.leadzai]: 'US',
  [themeNames.fcr]: 'BE',
  [themeNames.bizay]: 'PT',
  [themeNames.thryv]: 'US',
  [themeNames.youvia]: 'NL',
  [themeNames['yp-ca']]: 'CA',
  [themeNames.directel]: 'PT',
  [themeNames.puxenegc]: 'PT',
  [themeNames.inovcorp]: 'PT',
  [themeNames.mediamag]: 'DE',
  [themeNames['paginas-amarelas']]: 'PT',
  [themeNames['pag-amrl']]: 'PT',
  [themeNames.pblcrEst]: 'ES',
  [themeNames.fonecta]: 'EN',
  [themeNames.newmed21]: 'DE',
  [themeNames.herold]: 'DE',
  [themeNames.leadgen]: 'US',
  [themeNames['bee-ddt']]: 'ES',
  [themeNames.medialuo]: 'FI',
};

// FIXME: Redundant code
const backendThemeNamesMap: PartialRecord<ThemeName, boolean> = {};

Object.values(themeNames).forEach((themeName) => {
  backendThemeNamesMap[themeName] = true;
});

// FIXME: Move this utility function out of appConfig.ts
export function isThemeName(themeName: unknown): themeName is ThemeName {
  return isString(themeName) && themeName in backendThemeNamesMap;
}

const REACT_APP_THEME = import.meta.env.REACT_APP_THEME;

let theme: ThemeName = 'default';
if (REACT_APP_THEME != 'leadzai' && isThemeName(REACT_APP_THEME)) {
  theme = REACT_APP_THEME;
}

// FIXME: Move this utility function out of appConfig.ts
export function isCurrentAppTheme(themeName: ThemeName): boolean {
  return theme === themeName;
}

const version = 'v1';
const name = `web-app:${version}`;

/* ********** */
/* App Config */
/* ********** */
export const config = {
  name,
  theme,
  version,
  stripeKey,
  isLocalhost,
  facebookAppID,
  appEnvironment,
  googleMapsApiKey,
  googleMapsBoundaryMapId,
  googleMapsMapIds: GOOGLE_MAPS_MAP_IDS,
  posthogApiKey,
  posthogShouldTrack,
  release: import.meta.env.REACT_APP_RELEASE || 'local',
  muiXProLicenseKey:
    '181adecab5f5132f5882d3cddfb09bdaTz0xMjIyODMsRT0xNzk1NjUxMTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=',
  whitelabel_url:
    import.meta.env.REACT_APP_WHITELABELS_URL ||
    'https://storage.googleapis.com/advio-app-prod-master-image-storage/themes/',
  externalLinks: {
    help: 'https://advertio.com/support',
    termsOfUse: 'https://www.leadzai.com/terms',
    privacyPolicy: 'https://www.leadzai.com/privacy',
  },
  routes: {
    api: '/api/',
    atomic: '/api/atomic/',
    login: '/auth/login',
    logout: '/auth/logout',
  },
  cookies: {
    csrf: 'csrftoken',
  },
};
